import { zodResolver } from '@hookform/resolvers/zod'
import { useMutation } from '@tanstack/react-query'
import {
  createFileRoute,
  Link,
  redirect,
  useRouter,
} from '@tanstack/react-router'
import { useForm } from 'react-hook-form'
import { z } from 'zod'

import { ButtonLoading } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Form, FormField, FormItem, FormItemLayout } from '@/components/ui/form'
import { Input, InputPassword } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { AuthCard, GoogleLoginButton } from '@/components/view/auth-view'

import { authLogin } from '@/services/auth/auth.gql'
import { useAuth } from '@/services/auth/auth.store'
import { profileIsConfirmed } from '@/services/profile/profile.util'

import { GraphQLError } from '@/lib/gql-fetch'
import { toastCommonError } from '@/lib/utils'

const searchParams = z.object({
  redirect: z.string().optional(),
})

export const Route = createFileRoute('/auth/login')({
  validateSearch: searchParams,
  loaderDeps: ({ search }) => ({ search }),
  loader: async ({ deps }) => {
    const { isAuthenticated } = useAuth.getState()
    if (!isAuthenticated) return

    const isConfirmed = await profileIsConfirmed()
    if (!isConfirmed)
      throw redirect({
        to: '/auth/verification',
        replace: true,
      })

    throw redirect({
      to: deps.search?.redirect || '/',
      replace: true,
    })
  },
  component: RouteComponent,
})

function RouteComponent() {
  return (
    <AuthCard title="Masuk" desc="Halo, Selamat datang kembali di YES!">
      <LoginForm />
    </AuthCard>
  )
}

/* ---------------------------------- FORM ---------------------------------- */

type LoginDto = z.infer<typeof LoginDto>
const LoginDto = z.object({
  email: z.string().email(),
  password: z.string().min(1),
  remember: z.boolean().optional(),
})

function LoginForm() {
  const router = useRouter()
  const login = useAuth((s) => s.login)
  const form = useForm<LoginDto>({
    resolver: zodResolver(LoginDto),
    defaultValues: getDefaultValues(),
  })

  const { mutateAsync, isPending } = useMutation({
    mutationFn: async ({ email, password }: LoginDto) => {
      const result = await authLogin.api({
        request: { email, password },
      })

      await login(result.login.token)
      router.invalidate()
    },
    onError(error, variables, context) {
      if (GraphQLError.is(error)) {
        const code = error.getCode()
        const msg = error.msg
        if (code === 404)
          form.setError('email', { type: 'value', message: msg })
        else if (code === 401)
          form.setError('password', { type: 'value', message: msg })
        else toastCommonError(msg)
      } else {
        toastCommonError()
      }
    },
  })

  const handleOnSubmit = form.handleSubmit((data) => {
    mutateAsync(data)
  }, console.error)

  return (
    <Form {...form}>
      <form onSubmit={handleOnSubmit}>
        <fieldset className="space-y-4">
          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItemLayout label="Email">
                <Input placeholder="Masukkan Email" {...field} />
              </FormItemLayout>
            )}
          />
          <FormField
            control={form.control}
            name="password"
            render={({ field }) => (
              <FormItemLayout label="Password">
                <InputPassword placeholder="Masukkan Password" {...field} />
              </FormItemLayout>
            )}
          />
          <Link
            from={Route.fullPath}
            to="/auth/forgot-password"
            className="block w-fit text-sm underline"
          >
            Lupa password?
          </Link>
          <FormField
            control={form.control}
            name="remember"
            render={({ field }) => (
              <FormItem className="flex items-center gap-1.5">
                <Checkbox
                  id={field.name}
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
                <Label className="block pb-1" htmlFor={field.name}>
                  Simpan info login saya untuk sesi selanjutnya
                </Label>
              </FormItem>
            )}
          />
        </fieldset>
        <div className="mt-4 flex flex-col items-center gap-4">
          <ButtonLoading
            variant="yes"
            className="w-full font-bold"
            isLoading={isPending}
          >
            Masuk ke YES
          </ButtonLoading>
          <p className="text-sm">Atau masuk dengan menggunakan akun</p>
          <GoogleLoginButton />
          <p className="text-sm">
            Belum memiliki akun? Silakan&nbsp;
            <Link
              className="text-persian-red-600 text-sm font-bold hover:underline"
              from={Route.fullPath}
              to="/auth/register"
            >
              Daftar
            </Link>
          </p>
        </div>
      </form>
    </Form>
  )
}

function getDefaultValues(): LoginDto {
  return {
    email: '',
    password: '',
    remember: false,
  }
}
